import { useAsyncModal } from '@ipfabric/frontend-shared-assets/utils/react/useAsyncModal';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { Button } from 'components/Button/Button';
import { TabView } from 'components/Tabs/TabView';
import { useTabs } from 'components/Tabs/useTabs';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { showDanger } from 'containers/Toaster/actions';
import { get } from 'lodash';
import { DashboardLayoutTitleDialog } from 'pages/Dashboard/Overview/DashboardLayoutTitleDialog';
import {
  addLayout,
  clearDashboard,
  fetchDashboard,
  switchActiveLayout,
} from 'pages/Dashboard/Overview/actions';
import { makeSelectDashboard } from 'pages/Dashboard/Overview/selectors';
import { makeSelectedSnapshot } from 'pages/SnapshotManagement/selectors';
import PropTypes from 'prop-types';
import { Component, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withAppProps } from 'utils/react/withAppProps';
import Dashboard from './Dashboard';

@withAppProps
class OverviewTabContent extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    msg: PropTypes.object,
    onShowDanger: PropTypes.func.isRequired,
  };

  shouldComponentUpdate() {
    return true;
  }

  render() {
    const {
      msg: { dashboardOverview: dashboardMsg },
      msg,
    } = this.props;

    return <Dashboard {...this.props} msg={dashboardMsg} allMessages={msg} />;
  }
}

function OverviewTabs({
  tabs,
  activeTabKey,
  onSwitchActiveLayout,
  dashboard,
  onAddLayout,
}) {
  const addNewLayoutModal = useAsyncModal();

  const tabsHook = useTabs({
    tabs,
    activeTab: activeTabKey,
  });

  const handleChangeTab = useStableCallback((tabKey) => {
    onSwitchActiveLayout({
      layoutSlug: tabKey,
    });
    tabsHook.handleChangeTab(tabKey);
  });

  const handleAddTab = useStableCallback(async () => {
    const layoutTitle = await addNewLayoutModal.show();

    if (layoutTitle !== null) {
      onAddLayout({ layoutTitle });
    }
  });

  const layoutTitles = dashboard.layouts.map((layout) => layout.title);

  return (
    <>
      {addNewLayoutModal.render((returnFromModal) => (
        <DashboardLayoutTitleDialog
          mode='add'
          onClose={() => returnFromModal(null)}
          onConfirm={returnFromModal}
          existingLayoutTitles={layoutTitles}
        />
      ))}
      <TabView
        tabs={tabsHook.tabs}
        onChangeTab={(tabKey) => handleChangeTab(tabKey)}
        activeTab={tabsHook.activeTab}
        contentClassName='overflow-auto'
        variant='legacy'
        showAddButton={!dashboard.isEditLayoutMode}
        onAddTab={handleAddTab}
        initialActiveTab={undefined}
      />
    </>
  );
}

function Overview(props) {
  const msg = useLocale().dashboardOverview.dashboard;

  const addNewLayoutModal = useAsyncModal();

  const handleAddTabClick = useStableCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();

    const layoutTitle = await addNewLayoutModal.show();

    if (layoutTitle !== null) {
      props.onAddLayout({ layoutTitle });
    }
  });

  useEffect(() => {
    if (props.selectedSnapshot) {
      // fetch dashboard layouts once snapshot is selected
      props.onFetchDashboard();
    }
    return () => {
      // clear dashboard data when another snapshot is selected or user leaves the dashboard page
      props.onClearDashboard();
    };
  }, [props.selectedSnapshot, props.onFetchDashboard, props.onClearDashboard]);

  const activeTabKey = props.dashboard.layout?.slug ?? null;

  const tabs = useMemo(() => {
    return props.dashboard.layouts.map((layout) => {
      return {
        label: layout.title,
        key: layout.slug,
        disabled:
          props.dashboard.isEditLayoutMode && layout.slug !== activeTabKey,
        render: () => <OverviewTabContent {...props} />,
      };
    });
  }, [props, activeTabKey]);

  if (props.dashboard.dashboardLoading) {
    return (
      <div className='box box-solid mb-0 flex flex-1 items-center'>
        <LoaderAndText />
      </div>
    );
  }

  return (
    <>
      <Helmet title='Summary' />
      {addNewLayoutModal.render((returnFromModal) => (
        <DashboardLayoutTitleDialog
          mode='add'
          onClose={() => returnFromModal(null)}
          onConfirm={returnFromModal}
        />
      ))}
      {tabs.length ? (
        <>
          <h3 className='col-xs-8'>{get(msg, 'heading')}</h3>
          <OverviewTabs tabs={tabs} activeTabKey={activeTabKey} {...props} />
        </>
      ) : (
        <div className='flex h-full items-center justify-center'>
          <div className='dazzle__empty-dashboard'>
            <h3>Welcome to Dashboard!</h3>
            <p>
              Dashboard lets you know the network infrastructure status in one
              place.
            </p>
            <div className='text-center'>
              <Button
                type='button'
                variant='secondary'
                icon='fa-plus'
                onClick={handleAddTabClick}
              >
                Add New Panel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    onShowDanger: (args) => dispatch(showDanger(args)),
    onFetchDashboard: () => dispatch(fetchDashboard()),
    onSwitchActiveLayout: (args) => dispatch(switchActiveLayout(args)),
    onClearDashboard: (args) => dispatch(clearDashboard(args)),
    onAddLayout: (args) => dispatch(addLayout(args)),
  };
}

const mapStateToProps = createStructuredSelector({
  dashboard: makeSelectDashboard(),
  selectedSnapshot: makeSelectedSnapshot(),
});

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
